import { Button } from '../ui';
import type { PromptSummary } from '../../types';

// Icons
const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    strokeWidth="2"
    stroke="currentColor"
    className={`h-5 w-5 ${filled ? 'fill-purple-500 text-purple-500' : 'fill-none text-gray-400 group-hover:text-gray-600'}`}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

interface PromptSelectorBarProps {
  prompts: PromptSummary[];
  selectedPromptId: string | null;
  selectedPromptSummary: PromptSummary | null;
  isNewPromptMode: boolean;
  isPromptSaving: boolean;
  hasUnsavedChanges: boolean;
  editedNameValid: boolean;
  onSelectPrompt: (promptId: string) => void;
  onNewPrompt: () => void;
  onCancelNewPrompt: () => void;
  onCreatePrompt: () => void;
  onSavePrompt: () => void;
  onDeletePrompt: () => void;
  onSetActivePrompt: (promptId: string) => void;
}

export function PromptSelectorBar({
  prompts,
  selectedPromptId,
  selectedPromptSummary,
  isNewPromptMode,
  isPromptSaving,
  hasUnsavedChanges,
  editedNameValid,
  onSelectPrompt,
  onNewPrompt,
  onCancelNewPrompt,
  onCreatePrompt,
  onSavePrompt,
  onDeletePrompt,
  onSetActivePrompt,
}: PromptSelectorBarProps) {
  return (
    <div className="bg-white dark:bg-dark-elevated rounded-t-xl border border-b-0 border-gray-200 dark:border-dark-border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Prompt:</label>
          <select
            value={isNewPromptMode ? '__new__' : (selectedPromptId || '')}
            onChange={(e) => {
              if (e.target.value !== '__new__') {
                onSelectPrompt(e.target.value);
              }
            }}
            data-onboarding="prompt-dropdown"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-dark-border focus:border-purple-400 focus:ring-2 focus:ring-purple-400/40 outline-none bg-white dark:bg-dark-elevated text-gray-800 dark:text-gray-200 font-medium"
            style={{ minWidth: '250px' }}
          >
            <option value="">Select a prompt...</option>
            {isNewPromptMode && (
              <option value="__new__">+ New Prompt (unsaved)</option>
            )}
            {prompts.map((prompt) => (
              <option key={prompt.id} value={prompt.id}>
                {prompt.name} {prompt.is_active ? '★' : ''}
              </option>
            ))}
          </select>

          {isNewPromptMode ? (
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Creating new prompt...</span>
          ) : selectedPromptSummary ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => onSetActivePrompt(String(selectedPromptSummary.id))} data-onboarding="prompt-set-active-btn">
                {selectedPromptSummary.is_active ? (
                  <>
                    <StarIcon filled={true} />
                    <span className="ml-1">Active</span>
                  </>
                ) : (
                  <>
                    <StarIcon filled={false} />
                    <span className="ml-1">Set Active</span>
                  </>
                )}
              </Button>
              <Button variant="danger" size="sm" onClick={onDeletePrompt} disabled={isPromptSaving} data-onboarding="prompt-delete-btn">
                Delete
              </Button>
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {/* Save Hint */}
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 px-2">
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-hover border border-gray-300 dark:border-dark-border rounded text-[10px] font-mono text-gray-700 dark:text-gray-300">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+S
            </kbd>
            to save
          </div>

          {/* Unsaved Changes Alert */}
          {hasUnsavedChanges && !isPromptSaving && (
            <div className="px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 text-sm font-medium rounded-lg flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Unsaved changes
            </div>
          )}

          <Button variant="secondary" size="sm" onClick={onNewPrompt} leftIcon={<PlusIcon />} data-onboarding="new-prompt-btn">
            New Prompt
          </Button>

          {isNewPromptMode ? (
            <>
              <Button variant="secondary" size="sm" onClick={onCancelNewPrompt}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onCreatePrompt}
                isLoading={isPromptSaving}
                disabled={!editedNameValid}
              >
                {isPromptSaving ? 'Creating...' : 'Create Prompt'}
              </Button>
            </>
          ) : selectedPromptId ? (
            <Button
              variant="primary"
              size="sm"
              onClick={onSavePrompt}
              isLoading={isPromptSaving}
              disabled={!editedNameValid}
              data-onboarding="prompt-save-btn"
            >
              {isPromptSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
